import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PageLayout from '../../components/PageLayout';
import PageHero from '../../components/PageHero';
import Button from '../../components/Button';
import PerkCard from '../../components/PerkCard';
import ProcessIcon from '../../components/ProcessIcon';
import ProcessModal from '../../components/ProcessModal';
import VideoCard from '../../components/VideoCard';

const BREADCRUMBS = [
  { label: 'Home', to: '/' },
  { label: 'Employment', to: '/employment/ifys-recruitment' },
  { label: 'IFYS Careers' },
];

const PROCESS_ICONS = [
  {
    icon: (
      <svg className="w-14 h-14" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
      </svg>
    ),
    label: "Be the\nDifference",
    title: "Be the Difference",
    description: (
      <p className="mb-4">Whether you're working directly with individuals and families or supporting our frontline teams behind the scenes, every role at IFYS contributes to our mission of <strong>"Strengthening the Human Spirit"</strong>. Your contribution will help to create real, lasting change in the lives of others.</p>
    ),
  },
  {
    icon: (
      <svg className="w-14 h-14" fill="currentColor" viewBox="0 0 24 24">
        <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
      </svg>
    ),
    label: "Timeframe",
    title: "Timeframe",
    description: (
      <p className="mb-4">Our recruitment process typically takes between two to six weeks from application to offer. Roles in shared services tend to move at a faster pace, while frontline positions may take longer due to additional requirements, such as background checks, training, and qualification checks.</p>
    ),
  },
  {
    icon: (
      <svg className="w-14 h-14" fill="currentColor" viewBox="0 0 24 24">
        <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
      </svg>
    ),
    label: "Interviews",
    title: "Interviews",
    description: (
      <>
        <p className="mb-4">Your recruitment journey will typically include a phone interview followed by a panel interview. These are structured behavioural and situational interviews designed to explore your skills, experience, and alignment with the role you've applied for.</p>
        <p className="mb-4">Some positions may involve additional interview stages or use an on-demand video interview in place of a panel. We'll keep you informed throughout the process, so you know what to expect.</p>
      </>
    ),
  },
  {
    icon: (
      <svg className="w-14 h-14" fill="currentColor" viewBox="0 0 24 24">
        <path d="M20 18c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2H0v2h24v-2h-4zM4 6h16v10H4V6z"/>
      </svg>
    ),
    label: "Technology",
    title: "Technology",
    description: (
      <p className="mb-4">We use technology to help make your recruitment experience as smooth and efficient as possible. Depending on the role, interviews may be conducted via Microsoft Teams or on-demand videos, and reference checks may be completed using Referoo. Some positions may also involve skills testing to help us better understand your capabilities.</p>
    ),
  },
  {
    icon: (
      <svg className="w-14 h-14" fill="currentColor" viewBox="0 0 640 512">
        <path d="M323.4 85.2l-96.8 78.4c-16.1 13-19.2 36.4-7 53.1c12.9 17.8 38 21.3 55.3 7.8l99.3-77.2c7-5.4 17-4.2 22.5 2.8s4.2 17-2.8 22.5l-20.9 16.2L512 316.8V128h-.7l-3.9-2.5L434.8 79c-15.3-9.8-33.2-15-51.4-15c-21.8 0-43 7.5-60 21.2zm22.8 124.4l-51.7 40.2C263 274.4 217.3 268 193.7 235.6c-22.2-30.5-16.6-73.1 12.7-96.8l83.2-67.3c-11.6-4.9-24.1-7.4-36.8-7.4C234 64 215.7 69.6 200 80l-72 48V352h28.2l91.4 83.4c19.6 17.9 49.9 16.5 67.8-3.1c5.5-6.1 9.2-13.2 11.1-20.6l17 15.6c19.5 17.9 49.9 16.6 67.8-2.9c4.5-4.9 7.8-10.6 9.9-16.5c19.4 13 45.8 10.3 62.1-7.5c17.9-19.5 16.6-49.9-2.9-67.8l-134.2-123z"/>
      </svg>
    ),
    label: "Inclusive Hiring",
    title: "Inclusive Hiring",
    description: (
      <p className="mb-4">We value unique perspectives and experiences. We encourage applications from Aboriginal and Torres Strait Islander peoples, people of all genders and sexualities, individuals with disabilities, and those from diverse cultural and social backgrounds. If there's anything that could help make the recruitment process more accessible for you, please let us know, we'll consider how we can best support you.</p>
    ),
  },
  {
    icon: (
      <svg className="w-14 h-14" fill="currentColor" viewBox="0 0 24 24">
        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
        <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zM6 4h7v5h5v11H6V4z"/>
      </svg>
    ),
    label: "Suitability\nScreening",
    title: "Suitability Screening",
    description: (
      <p className="mb-4">At the reference checking stage, we will conduct two reference checks with your current and past line managers and complete a National Police Check at no cost to you. All IFYS roles require you to obtain a Blue Card (Queensland Working with Children Check) and a Licensed Care Service Suitability Screening. Disability Services roles will also require a NDIS Workers Screening Check.</p>
    ),
  },
  {
    icon: (
      <svg className="w-14 h-14" fill="currentColor" viewBox="0 0 24 24">
        <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-8 12H9v-2h2v2zm0-4H9V7h2v4zm4 4h-2v-2h2v2zm0-4h-2V7h2v4z"/>
      </svg>
    ),
    label: "Additional\nRequirements",
    title: "Additional Requirements",
    description: (
      <p className="mb-4">Some roles may require you to hold specific Australian qualifications or to complete specific training prior to employment. E.g. The Minimum Qualification Standard applies to all Non-Family Based Care roles.</p>
    ),
  },
  {
    icon: (
      <svg className="w-14 h-14" fill="currentColor" viewBox="0 0 24 24">
        <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-3 12H7c-.55 0-1-.45-1-1s.45-1 1-1h10c.55 0 1 .45 1 1s-.45 1-1 1zm0-3H7c-.55 0-1-.45-1-1s.45-1 1-1h10c.55 0 1 .45 1 1s-.45 1-1 1zm0-3H7c-.55 0-1-.45-1-1s.45-1 1-1h10c.55 0 1 .45 1 1s-.45 1-1 1z"/>
      </svg>
    ),
    label: "Communication",
    title: "Communication",
    description: (
      <p className="mb-4">Throughout your recruitment journey, you'll hear from our Recruitment and Licensing teams via phone and email. To make sure you don't miss any updates, please keep an eye on your inbox (remember to check your junk or spam folder for notifications).</p>
    ),
  },
];

const VIDEO_DATA = [
  {
    title: "A Career That Makes a Difference | IFYS",
    name: "Ernest Suavai",
    role: "Regional Manager Non-Family Based Care",
    youtubeUrl: "https://youtu.be/JF2BqBXE8e0?si=kGHqoRXoqkA6VvrJ",
  },
  {
    title: "Work with Purpose | IFYS",
    name: "Work with Purpose",
    role: "Join the Team at IFYS",
    youtubeUrl: "https://www.youtube.com/watch?v=qoSyCEaTueg",
  },
  {
    title: "IFYS Youth Transitional Services Team Nat...",
    name: "Nathan Andrews",
    role: "Youth Transition Services",
    youtubeUrl: "https://youtu.be/4qdQUQ19gs0?si=R4CafmDCDKUwkYrI",
  },
];

const IfysRecruitmentPage = () => {
  const navigate = useNavigate();
  const [selectedProcess, setSelectedProcess] = useState(null);

  return (
    <PageLayout activeLink="employment">
      <PageHero
        title="IFYS Careers"
        subtitle="Be a part of our legacy of care across Queensland — join a team that puts people first."
        backgroundImage="/images/thumb2.jpg"
        breadcrumbs={BREADCRUMBS}
        variant="split"
      />

      {/* Strengthening the Human Spirit — matches home WhoWeAre band */}
      <section className="relative bg-white py-16 md:py-24 overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-ifys-blue mb-6 md:mb-8 tracking-tight">
          Strengthening the Human Spirit
        </h2>
        <div className="space-y-4 text-ifys-blue text-base md:text-lg leading-relaxed">
          <p>
            For over 45 years IFYS has pursued opportunities that create change, develop awareness and strengthen the human spirit. To achieve this, we seek out exceptional people with aligned values to deliver services that support the vulnerable member of our communities.
          </p>
          <p>
            We recognise our people make the difference, and as such, we continuously assess and enhance our framework of support. We readily implement new practices and procedures, such as the Sanctuary Model of Care, which actively promotes emotional, social, moral, and physical well-being for both our staff and service users.
          </p>
        </div>
        <div className="mt-8">
          <Button size="lg" onClick={() => navigate('/employment/current-vacancies')}>
            CURRENT VACANCIES
          </Button>
        </div>
        </div>
      </section>

      {/* Our Benefits — matches home ServicesBar / Urban Angels grey band */}
      <section className="relative py-16 md:py-24 px-4 sm:px-6 lg:px-8 overflow-hidden bg-ifys-light-gray border-t border-gray-200">
        <div className="absolute -top-32 -right-32 w-64 h-64 sm:w-96 sm:h-96 opacity-100 hidden md:block">
          <img src="/images/image.png" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="absolute -bottom-32 -left-32 w-64 h-64 sm:w-96 sm:h-96 opacity-100 hidden md:block">
          <img src="/images/image.png" alt="" className="w-full h-full object-cover" />
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-ifys-blue mb-8 sm:mb-10 md:mb-12 text-center tracking-tight">
            Our Benefits
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 items-stretch">
            <PerkCard
              title="Unique Training"
              description={[
                "You'll have access to role specific and specialised training opportunities, delivered by our in house training team and subject matter experts. Opportunities may include: Therapeutic Crisis Intervention, Maybo, Sanctuary Model and Preventing Child Sexual Exploitation.",
              ]}
              variant="dark"
            />
            <PerkCard
              title="Salary Packaging"
              description={[
                "We offer not-for-profit salary packaging options for all staff to increase your take home pay, you can package up to $15,900 per FBT year and access an additional $2,650 to package towards meal and entertainment.",
              ]}
              variant="light"
            />
            <PerkCard
              title="Sanctuary Model"
              description={[
                "The Sanctuary Model embeds a trauma-informed framework within the organisation, that promotes emotional, social, and physical safety. Through Sanctuary, we introduce practical tools and shared language that strengthen teams to navigate stress, build resilience, and foster a positive, inclusive culture.",
              ]}
              variant="dark"
            />
            <PerkCard
              title="Wellbeing"
              description={[
                "We support your wellbeing with access to the Calm Premium app, our Employee Assistance Program, and Flight Centre discounts.",
              ]}
              variant="light"
            />
          </div>
        </div>
      </section>

      {/* What to expect when you apply */}
      <section className="relative bg-white py-16 md:py-24 px-4 sm:px-6 lg:px-8 overflow-hidden border-t border-gray-200">
        <div className="absolute -top-20 -right-20 w-80 h-80 opacity-100 rotate-45 hidden lg:block">
          <img src="/images/plant.png" alt="" className="w-full h-full object-contain" />
        </div>
        <div className="absolute -bottom-20 -left-20 w-80 h-80 opacity-100 rotate-45 scale-x-[-1] scale-y-[-1] hidden lg:block">
          <img src="/images/plant.png" alt="" className="w-full h-full object-contain" />
        </div>

        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-ifys-blue mb-4 sm:mb-6 text-center tracking-tight px-2">
            What to expect when you apply
          </h2>
          <div className="max-w-5xl mx-auto text-center mb-12 sm:mb-14 md:mb-16 font-normal px-4 sm:px-6 md:px-8 mt-6 sm:mt-8">
            <p className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed mb-4 sm:mb-5">
              We are deeply committed to the wellbeing, safety and dignity of every individual who accesses our services, including children, young people, adults and people with disabilities. As part of this commitment, we uphold best practice standards and adhere to the Human Services Quality Framework, NDIS Practice Standards and National Principles for Child Safe Organisations.
            </p>
            <p className="text-sm sm:text-base md:text-lg text-gray-700 leading-relaxed">
              We want every candidate to feel safe, respected and valued throughout the recruitment process. Our aim is to provide a fair and inclusive experience where you can confidently showcase your skills, experience and potential.
            </p>
          </div>

          {/* Desktop layout */}
          <div className="max-w-5xl mx-auto px-4 sm:px-6 md:px-8">
            <div className="hidden lg:flex items-start" style={{ justifyContent: 'space-between', paddingLeft: '1.5rem', paddingRight: '1.5rem' }}>
              <div className="grid grid-cols-4 gap-x-12 gap-y-10">
                {PROCESS_ICONS.map((item, index) => (
                  <ProcessIcon
                    key={index}
                    icon={item.icon}
                    label={item.label}
                    onClick={() => setSelectedProcess(item)}
                  />
                ))}
              </div>
              <div className="flex" style={{ height: '18.5rem' }}>
                <img
                  src="/images/man posing.png"
                  alt="Employee portrait"
                  className="h-full w-auto object-contain"
                />
              </div>
            </div>
          </div>

          {/* Mobile & Tablet layout */}
          <div className="flex lg:hidden flex-col items-center gap-8 w-full">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-16 sm:gap-x-20 md:gap-x-10 gap-y-6 sm:gap-y-8 px-4 w-full max-w-md md:max-w-none">
              {PROCESS_ICONS.map((item, index) => (
                <ProcessIcon
                  key={index}
                  icon={item.icon}
                  label={item.label}
                  onClick={() => setSelectedProcess(item)}
                />
              ))}
            </div>
            <div className="flex justify-center w-full px-4">
              <img
                src="/images/man posing.png"
                alt="Employee portrait"
                className="w-48 sm:w-56 md:w-64 h-auto object-cover rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Video Testimonials */}
      <section className="relative py-16 md:py-24 px-4 sm:px-6 lg:px-8 overflow-hidden bg-ifys-light-gray border-t border-gray-200">
        <div className="absolute -top-32 -left-32 w-64 h-64 sm:w-96 sm:h-96 opacity-100 hidden md:block">
          <img src="/images/image.png" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="absolute -bottom-32 -right-32 w-64 h-64 sm:w-96 sm:h-96 opacity-100 hidden md:block">
          <img src="/images/image.png" alt="" className="w-full h-full object-cover" />
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-ifys-blue mb-3 sm:mb-4 text-center tracking-tight px-2">
            Hear it from our employees!
          </h2>
          <p className="text-base sm:text-lg text-gray-700 mb-8 sm:mb-10 md:mb-12 text-center px-2 leading-relaxed">
            Listen to the first hand experience of our valued employees
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8 items-stretch">
            {VIDEO_DATA.map((video, index) => (
              <VideoCard key={index} {...video} />
            ))}
          </div>
        </div>
      </section>

      {/* Explore Employment */}
      <section className="bg-white border-t border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 md:py-24">
          <h2 className="text-center text-ifys-blue text-2xl sm:text-3xl font-bold mb-10">
            Explore Employment
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 max-w-3xl mx-auto">
            <Link
              to="/employment/current-vacancies"
              className="group relative block rounded-2xl overflow-hidden bg-white border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
            >
              <div className="px-8 py-10 flex flex-col items-center text-center gap-4">
                <div className="w-16 h-16 rounded-full bg-ifys-blue/10 flex items-center justify-center group-hover:bg-ifys-blue/20 transition-colors duration-300">
                  <svg className="w-8 h-8 text-ifys-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-ifys-blue">Current Vacancies</h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                  Browse our latest job openings across Queensland.
                </p>
                <span className="inline-flex items-center gap-1 text-sm font-semibold text-ifys-blue group-hover:gap-2 transition-all duration-200">
                  View jobs
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </span>
              </div>
            </Link>
            <Link
              to="/employment/volunteers"
              className="group relative block rounded-2xl overflow-hidden bg-white border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
            >
              <div className="px-8 py-10 flex flex-col items-center text-center gap-4">
                <div className="w-16 h-16 rounded-full bg-ifys-blue/10 flex items-center justify-center group-hover:bg-ifys-blue/20 transition-colors duration-300">
                  <svg className="w-8 h-8 text-ifys-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-ifys-blue">Volunteers</h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                  Make a difference through volunteering with IFYS.
                </p>
                <span className="inline-flex items-center gap-1 text-sm font-semibold text-ifys-blue group-hover:gap-2 transition-all duration-200">
                  Learn more
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      <ProcessModal
        isOpen={selectedProcess !== null}
        onClose={() => setSelectedProcess(null)}
        title={selectedProcess?.title || ''}
        content={selectedProcess?.description || ''}
      />
    </PageLayout>
  );
};

export default IfysRecruitmentPage;
